import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const features = [
  {
    icon: 'ri-dashboard-3-line',
    title: 'Stav projektu',
    text: 'Vidíte presne, v akej fáze je váš web, e-shop alebo kampaň. Žiadne hádanie, žiadne čakanie na správy.',
    accent: 'text-orange-400',
    bg: 'bg-orange-500/8',
    border: 'border-orange-500/15',
    size: 'md:col-span-2',
  },
  {
    icon: 'ri-task-line',
    title: 'Úlohy a termíny',
    text: 'Potrebujeme od vás fotky, texty alebo schválenie? Vidíte to priamo tu - s termínom a kontextom.',
    accent: 'text-amber-400',
    bg: 'bg-amber-500/8',
    border: 'border-amber-500/15',
    size: '',
  },
  {
    icon: 'ri-service-line',
    title: 'Objednané služby',
    text: 'Prehľad všetkých aktívnych aj ukončených služieb - vrátane ceny a dátumu predĺženia.',
    accent: 'text-emerald-400',
    bg: 'bg-emerald-500/8',
    border: 'border-emerald-500/15',
    size: '',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Mesačné reporty',
    text: 'Každý mesiac dostanete report so všetkými dôležitými číslami - vo formáte PDF na stiahnutie.',
    accent: 'text-sky-400',
    bg: 'bg-sky-500/8',
    border: 'border-sky-500/15',
    size: '',
  },
  {
    icon: 'ri-bill-line',
    title: 'Faktúry na jednom mieste',
    text: 'Celá história faktúr prehľadne usporiadaná. Koniec hľadaniu v emailoch.',
    accent: 'text-violet-400',
    bg: 'bg-violet-500/8',
    border: 'border-violet-500/15',
    size: '',
  },
  {
    icon: 'ri-message-3-line',
    title: 'Priama komunikácia',
    text: 'Máte otázku k projektu? Napíšte nám priamo v portáli. Bez emailových vlákien, bez strateného kontextu.',
    accent: 'text-rose-400',
    bg: 'bg-rose-500/8',
    border: 'border-rose-500/15',
    size: 'md:col-span-2',
  },
];

const steps = [
  {
    num: '01',
    icon: 'ri-shopping-cart-line',
    title: 'Objednajte si akúkoľvek službu',
    text: 'Stačí nás kontaktovať a dohodnúť sa na spolupráci. Portál dostanete bez ohľadu na to, či ide o jednorazový projekt alebo mesačnú správu.',
  },
  {
    num: '02',
    icon: 'ri-key-2-line',
    title: 'Dostanete prístup do 24 hodín',
    text: 'Po potvrdení objednávky vám pošleme prihlasovacie údaje na váš email. Žiadne sťahovania, žiadna inštalácia - funguje v prehliadači.',
  },
  {
    num: '03',
    icon: 'ri-eye-line',
    title: 'Sledujte všetko v reálnom čase',
    text: 'Prihlásenie, a hneď vidíte aktuálny stav. Aktualizujeme portál pri každej zmene v projekte - nie raz za mesiac.',
  },
];

export default function KPFeatures() {
  const { ref: featRef, inView: featInView } = useInView();
  const { ref: procRef, inView: procInView } = useInView();

  return (
    <>
      {/* Features section */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-6xl mx-auto px-6">
          <div ref={featRef} className={`text-center mb-14 transition-all duration-700 ${featInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-white border border-zinc-200 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-layout-grid-line text-orange-500 text-sm" />
              <span className="text-zinc-600 text-sm font-semibold">Čo nájdete v portáli</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-zinc-900 mb-3 leading-tight">
              Všetko, čo potrebujete vedieť{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                o vašom projekte
              </span>
            </h2>
            <p className="text-zinc-500 text-base max-w-xl mx-auto">
              Portál nie je len prehľad - je to váš pracovný priestor, kde prebieha celá spolupráca.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div
                key={f.title}
                className={`${f.size} bg-white border ${f.border} rounded-2xl p-7 transition-all duration-500 ${featInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${f.bg} border ${f.border} mb-5`}>
                  <i className={`${f.icon} ${f.accent} text-xl`} />
                </div>
                <h3 className="text-zinc-900 font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process section */}
      <section className="py-24 bg-zinc-950 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(249,115,22,0.07) 0%, transparent 60%)' }}
        />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div ref={procRef} className={`text-center mb-14 transition-all duration-700 ${procInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-route-line text-orange-400 text-sm" />
              <span className="text-zinc-300 text-sm font-semibold">Ako to funguje</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3 leading-tight">
              Tri kroky k vlastnému portálu
            </h2>
            <p className="text-zinc-500 text-base max-w-lg mx-auto">
              Prístup do portálu nie je platená funkcia. Dostane ho každý klient automaticky.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {steps.map((step, i) => (
              <div
                key={step.num}
                className={`relative bg-zinc-900/60 border border-zinc-800 rounded-2xl p-7 transition-all duration-700 ${procInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 -right-3 w-6 h-px bg-zinc-700 z-10" />
                )}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-orange-500/10 border border-orange-500/20">
                    <i className={`${step.icon} text-orange-400 text-xl`} />
                  </div>
                  <span className="text-zinc-700 font-black text-2xl">{step.num}</span>
                </div>
                <h3 className="text-white font-bold text-base mb-2">{step.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>

          {/* CTA strip */}
          <div className={`bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-700 delay-300 ${procInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div>
              <h3 className="text-white text-2xl md:text-3xl font-black mb-2 leading-tight">
                Začnite spolupracovať s Boostio
              </h3>
              <p className="text-orange-100 text-sm leading-relaxed max-w-md">
                Prístup do klientskeho portálu dostanete zadarmo k akejkoľvek objednanej službe.
                Bezplatná konzultácia vás k ničomu nezaväzuje.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link
                to="/#kontakt"
                className="inline-flex items-center gap-2 bg-white text-orange-600 font-bold px-7 py-3.5 rounded-xl transition-all duration-200 hover:bg-orange-50 cursor-pointer whitespace-nowrap"
              >
                Konzultácia zadarmo
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/sluzby/weby"
                className="inline-flex items-center gap-2 bg-orange-600/40 hover:bg-orange-600/60 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap border border-white/20"
              >
                Pozrieť služby
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
